// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// Month-end close (D-FIN-1), built to the QoE / GAAP requirements.
//
// The screen's job is to make ONE distinction impossible to miss: a closed period
// is a fixed number, an open one is not. Everything else is a table.

import { useEffect, useState } from 'react'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { Lock, Unlock, Download, AlertTriangle, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'

const card: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: 16,
  background: 'var(--color-surface, #fff)', marginBottom: 16,
}
const lbl: React.CSSProperties = {
  fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
  color: 'var(--color-text-muted)', fontWeight: 600,
}
const th: React.CSSProperties = {
  textAlign: 'left', padding: '7px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: 0.4, color: 'var(--color-text-muted)', fontWeight: 600,
  borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
}
const td: React.CSSProperties = {
  padding: '6px 10px', fontSize: 13, borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
}
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', fontSize: 13,
  fontWeight: 600, cursor: 'pointer', borderRadius: 6, border: '1px solid var(--color-border)',
  background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
}
const money = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

// D-UI-2 funnel columns. Money is the number rounded to the cents the cell prints; dates are
// raw, so the funnel compares by day. NDC and Rx # tick values: a Min/Max over an identifier
// means nothing.
const cents = (n: number | null | undefined) => (n == null ? null : Math.round(n * 100) / 100)

const REVENUE_COLUMNS: FunnelColumn<api.MonthEndRow>[] = [
  { key: 'ndc', label: 'NDC', value: r => r.ndc, kind: 'list' },
  { key: 'drug', label: 'Drug', value: r => r.drug },
  { key: 'rx_number', label: 'Rx #', value: r => r.rx_number, kind: 'list' },
  { key: 'fill_number', label: 'Fill', value: r => r.fill_number },
  { key: 'prescriber', label: 'Prescriber', value: r => r.prescriber },
  { key: 'payer', label: 'Payer', value: r => r.payer },
  { key: 'shipped_at', label: 'Shipped', value: r => r.shipped_at },
  { key: 'patient_responsibility', label: 'Patient responsibility', value: r => cents(r.patient_responsibility) },
  { key: 'acquisition_cost', label: 'Acq cost', value: r => cents(r.acquisition_cost) },
  { key: 'revenue', label: 'Revenue', value: r => cents(r.revenue) },
  { key: 'gross_profit', label: 'Gross profit', value: r => cents(r.gross_profit) },
]
const LIABILITY_COLUMNS: FunnelColumn<api.BilledNotShippedRow>[] = [
  { key: 'rx', label: 'Rx #', value: r => r.rx, kind: 'list' },
  { key: 'drug', label: 'Drug', value: r => r.drug },
  { key: 'payer', label: 'Payer', value: r => r.payer },
  { key: 'date_filled', label: 'Filled', value: r => r.date_filled },
  { key: 'amount_paid', label: 'Amount paid', value: r => cents(r.amount_paid) },
]

export default function MonthEndClosePage() {
  const [periods, setPeriods] = useState<api.MonthEndPeriod[]>([])
  const [period, setPeriod] = useState<string>('')
  const [rep, setRep] = useState<api.MonthEndRevenue | null>(null)
  const [liab, setLiab] = useState<Awaited<ReturnType<typeof api.getBilledNotShipped>> | null>(null)
  const [tab, setTab] = useState<'revenue' | 'liability'>('revenue')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // The funnels filter the WHOLE period, then the table keeps the first 300 of what matches.
  const fxRev = useTableFunnels(rep?.rows, REVENUE_COLUMNS)
  const fxLiab = useTableFunnels(liab?.rows, LIABILITY_COLUMNS)

  const readErr = (e: unknown) => {
    const b = (e as { body?: { message?: string; error?: string } }).body
    return b?.message || b?.error || (e as Error).message || 'Request failed.'
  }

  const loadPeriod = async (p: string) => {
    setBusy(true); setErr(null)
    try { setRep(await api.getMonthEndRevenue(p)); setPeriod(p) }
    catch (e) { setErr(readErr(e)) } finally { setBusy(false) }
  }

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.getMonthEndPeriods()
        setPeriods(r.items)
        if (r.items[0]) await loadPeriod(r.items[0].period)
      } catch (e) { setErr(readErr(e)) }
      try { setLiab(await api.getBilledNotShipped(new Date().getFullYear())) } catch { /* optional */ }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function doClose() {
    if (!period) return
    setBusy(true); setErr(null)
    try {
      await api.closeMonthEnd(period, 'Month-end close.')
      setPeriods(await api.getMonthEndPeriods().then(r => r.items))
      await loadPeriod(period)
    } catch (e) { setErr(readErr(e)) } finally { setBusy(false) }
  }

  // CSV straight from what is on screen — auditors ask for the file, and this is
  // the same data, not a second query that could disagree with it.
  function exportCsv() {
    if (!rep) return
    const head = ['NDC', 'Drug', 'Rx #', 'Fill #', 'Prescriber', 'Payer', 'Shipped',
                  'Patient responsibility', 'Acquisition cost', 'Adjudication fees', 'Revenue', 'Gross profit']
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const body = rep.rows.map(r => [
      r.ndc, r.drug, r.rx_number, r.fill_number, r.prescriber, r.payer,
      r.shipped_at ? new Date(r.shipped_at).toLocaleDateString() : '',
      r.patient_responsibility, r.acquisition_cost,
      r.adjudication_fees ?? '', r.revenue, r.gross_profit,
    ].map(esc).join(','))
    const csv = [head.map(esc).join(','), ...body].join('\r\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `shipped-revenue-${rep.period}${rep.closed ? '-CLOSED' : '-PROVISIONAL'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const closedNow = !!rep?.closed

  return (
    <div style={{ maxWidth: 'var(--page-max, 100%)', width: '100%', margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        {closedNow ? <Lock size={22} /> : <Unlock size={22} />}
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Month-end close</h1>
      </div>
      <p style={{ fontSize: 13.5, color: 'var(--color-text-muted)', marginTop: 0, marginBottom: 16 }}>
        Closing a month writes its figures down. A closed period is served from that snapshot and is
        never recomputed, so prior months cannot move.
      </p>

      {err && (
        <div style={{ ...card, borderColor: 'var(--color-danger, #b91c1c)' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 13 }}>{err}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: 18 }}>
        {([['revenue', 'Shipped revenue'], ['liability', 'Billed, not shipped']] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            style={{ padding: '9px 16px', fontSize: 14, fontWeight: tab === k ? 700 : 500, background: 'none',
              cursor: 'pointer', border: 'none', color: tab === k ? 'var(--color-text)' : 'var(--color-text-muted)',
              borderBottom: tab === k ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: -1 }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'revenue' && (
        <>
          <div style={card}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={lbl}>Period</div>
                <SearchableSelect
                  value={period}
                  ariaLabel="Period"
                  style={{ marginTop: 4 }}
                  options={periods.map(p => ({
                    value: p.period,
                    label: `${p.period} · ${p.shipments} shipped`,
                    hint: p.closed ? 'CLOSED' : undefined,
                  }))}
                  onChange={v => void loadPeriod(v)} />
              </div>
              <div style={{ flex: 1 }} />
              <button type="button" style={btn} disabled={busy} onClick={exportCsv}>
                <Download size={14} /> CSV
              </button>
              {!closedNow && (
                <button type="button" style={{ ...btn, fontWeight: 700 }} disabled={busy} onClick={doClose}>
                  <Lock size={14} /> {busy ? 'Closing…' : `Close ${period}`}
                </button>
              )}
              {closedNow && (
                <button type="button" style={btn} disabled={busy} onClick={() => void loadPeriod(period)}>
                  <RefreshCw size={14} /> Re-read snapshot
                </button>
              )}
            </div>

            {/* The one thing that must never be ambiguous. */}
            {rep && (
              <div style={{
                marginTop: 12, padding: '10px 12px', borderRadius: 6, fontSize: 13,
                border: '1px solid var(--color-border)',
                borderLeft: `3px solid ${closedNow ? 'var(--color-success, #15803d)' : 'var(--color-warning, #ca8a04)'}`,
                background: 'var(--color-bg, #f8fafc)',
              }}>
                <b>{closedNow ? 'Closed' : 'Open — provisional'}</b>
                {rep.close?.closed_at ? ` · locked ${new Date(rep.close.closed_at).toLocaleString()}` : ''}
                <div style={{ color: 'var(--color-text-muted)', marginTop: 3 }}>{rep.basis}</div>
              </div>
            )}
          </div>

          {rep && (
            <>
              <div style={{ ...card, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                <Stat label="Fills shipped" value={String(rep.totals.fill_count)} />
                <Stat label="Revenue" value={money(rep.totals.revenue)} />
                <Stat label="Acquisition cost" value={money(rep.totals.acquisition)} />
                <Stat label="Patient responsibility" value={money(rep.totals.patient_responsibility)} />
                <Stat label="Gross profit" value={money(rep.totals.gross_profit)} strong />
                <Stat label="Margin" value={rep.totals.gross_margin_pct == null ? '—' : `${rep.totals.gross_margin_pct}%`} />
              </div>

              {!rep.adjudication_fees_available && (
                <div style={{ ...card, fontSize: 12.5, color: 'var(--color-text-muted)' }}>
                  <b style={{ color: 'var(--color-text)' }}>Adjudication fees are not populated.</b>{' '}
                  {rep.adjudication_fees_note}
                </div>
              )}

              {fxRev.chips}
              <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table {...fxRev.tableProps} style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>NDC {fxRev.funnel('ndc')}</th><th style={th}>Drug {fxRev.funnel('drug')}</th><th style={th}>Rx # {fxRev.funnel('rx_number')}</th>
                      <th style={th}>Fill {fxRev.funnel('fill_number')}</th><th style={th}>Prescriber {fxRev.funnel('prescriber')}</th><th style={th}>Payer {fxRev.funnel('payer')}</th>
                      <th style={th}>Shipped {fxRev.funnel('shipped_at')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>Patient {fxRev.funnel('patient_responsibility')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>Acq cost {fxRev.funnel('acquisition_cost')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>Revenue {fxRev.funnel('revenue')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>Gross profit {fxRev.funnel('gross_profit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fxRev.rows.slice(0, 300).map((r, i) => (
                      <tr key={`${r.rx}-${i}`}>
                        <td style={td}>{r.ndc ?? '—'}</td>
                        <td style={td}>{r.drug ?? '—'}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{r.rx_number ?? '—'}</td>
                        <td style={td}>{r.fill_number ?? '—'}</td>
                        <td style={td}>{r.prescriber ?? '—'}</td>
                        <td style={td}>{r.payer ?? '—'}</td>
                        <td style={td}>{r.shipped_at ? new Date(r.shipped_at).toLocaleDateString() : '—'}</td>
                        <td style={num}>{money(r.patient_responsibility)}</td>
                        <td style={num}>{money(r.acquisition_cost)}</td>
                        <td style={num}>{money(r.revenue)}</td>
                        <td style={{ ...num, fontWeight: 600 }}>{money(r.gross_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {fxRev.rows.length > 300 && (
                  <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {fxRev.active
                      ? <>Showing the first 300 of {fxRev.rows.length} fills that match the filters ({rep.rows.length} in the period) — the CSV contains all {rep.rows.length}, unfiltered.</>
                      : <>Showing the first 300 of {rep.rows.length} fills — the CSV contains all of them.</>}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'liability' && liab && (
        <>
          <div style={{ ...card, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            <Stat label="Fills" value={String(liab.totals.count)} />
            <Stat label="Billed and paid, not shipped" value={money(liab.totals.amount_paid)} strong />
          </div>
          <div style={{ ...card, fontSize: 12.5, color: 'var(--color-text-muted)' }}>
            <b style={{ color: 'var(--color-text)' }}>{liab.treatment}</b>
            <div style={{ marginTop: 4 }}>{liab.basis} Excluded: {liab.excluded.toLowerCase()}</div>
          </div>
          {fxLiab.chips}
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table {...fxLiab.tableProps} style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Rx # {fxLiab.funnel('rx')}</th><th style={th}>Drug {fxLiab.funnel('drug')}</th><th style={th}>Payer {fxLiab.funnel('payer')}</th>
                  <th style={th}>Filled {fxLiab.funnel('date_filled')}</th><th style={{ ...th, textAlign: 'right' }}>Amount paid {fxLiab.funnel('amount_paid')}</th>
                </tr>
              </thead>
              <tbody>
                {fxLiab.rows.slice(0, 300).map((r, i) => (
                  <tr key={`${r.rx}-${i}`}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.rx ?? '—'}</td>
                    <td style={td}>{r.drug ?? '—'}</td>
                    <td style={td}>{r.payer ?? '—'}</td>
                    <td style={td}>{r.date_filled ? new Date(r.date_filled).toLocaleDateString() : '—'}</td>
                    <td style={num}>{money(r.amount_paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fxLiab.rows.length > 300 && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                {fxLiab.active
                  ? <>Showing the first 300 of {fxLiab.rows.length} that match the filters ({liab.rows.length} in total).</>
                  : <>Showing the first 300 of {liab.rows.length}.</>}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: strong ? 21 : 18, fontWeight: strong ? 700 : 600, marginTop: 2,
        fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  )
}
